/**
 * Data hooks for the Schedule module.
 *
 * `useScheduleRules` replaces the localStorage mock: it loads the tenant's
 * rules once, and every mutation is a request whose result replaces the
 * row in place. No optimistic list rewriting — a rule that fails
 * validation must not linger on screen as if it had been saved.
 *
 * `useStrategyFeed` gives the Calendar the real strategies. If the call
 * fails it returns `unavailable`, and the Calendar says so: five invented
 * strategies next to real rules is worse than an honest gap.
 */
import { useCallback, useEffect, useState } from 'react'
import useToast from '@hooks/useToast'
import { scheduleApi } from './api'
import { rules as ruleAdapter, strategyForCalendar } from './adapters'

function describe(error) {
	if (error?.name !== 'ApiError') return error?.message || 'Unexpected error'
	const d = error.details
	if (error.code === 'unknown_office' && d?.office_codes?.length) {
		return `${error.message}: ${d.office_codes.join(', ')}`
	}
	if (error.code === 'validation_error' && d?.errors?.length) {
		return `${error.message}: ${d.errors.map(e => `${e.loc} ${e.msg}`).join('; ')}`
	}
	return error.message
}

export function useScheduleRules() {
	const { showToast } = useToast()
	const [rules, setRules] = useState([])
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState(null)

	const reload = useCallback(async () => {
		setLoading(true)
		try {
			setRules(await scheduleApi.rules.list())
			setError(null)
		} catch (e) {
			setError(e)
			showToast.error(describe(e))
		} finally {
			setLoading(false)
		}
	}, [])

	useEffect(() => { reload() }, [reload])

	/** Create or update; returns the saved rule, or throws for the caller
	 *  to keep the drawer open with the operator's input intact. */
	const save = useCallback(async (rule) => {
		const dto = ruleAdapter.toServer(rule)
		const saved = rule.id
			? await scheduleApi.rules.update(rule.id, dto)
			: await scheduleApi.rules.create(dto)
		setRules(prev => rule.id
			? prev.map(r => (r.id === saved.id ? saved : r))
			: [...prev, saved].sort((a, b) => a.priority - b.priority || a.name.localeCompare(b.name)))
		return saved
	}, [])

	const remove = useCallback(async (id) => {
		try {
			await scheduleApi.rules.remove(id)
			setRules(prev => prev.filter(r => r.id !== id))
		} catch (e) {
			showToast.error(describe(e))
		}
	}, [])

	const setActive = useCallback(async (id, isActive) => {
		try {
			const saved = await scheduleApi.rules.setActive(id, isActive)
			setRules(prev => prev.map(r => (r.id === id ? saved : r)))
		} catch (e) {
			showToast.error(describe(e))
		}
	}, [])

	/** Launch a rule by hand. Returns the engine's report or throws, so the
	 *  caller can show the count — searches are billed and the operator
	 *  should see the number they just spent. */
	const runNow = useCallback(async (id, reason) => {
		try {
			return await scheduleApi.rules.runNow(id, reason)
		} catch (e) {
			showToast.error(describe(e))
			throw e
		}
	}, [])

	return { rules, loading, error, reload, save, remove, setActive, runNow, describeError: describe }
}

/**
 * The preview: what the configuration would launch, for one rule or for
 * whatever is due now. Refetched on demand, never on a timer — it is a
 * question the operator asks, not a dashboard.
 */
export function useSweepPreview() {
	const [state, setState] = useState({ raw: null, loading: false, error: null, ruleId: null })

	// Returns `{ raw, error }` as well as filling the panel: "Run now"
	// asks the same question to build its confirmation, and needs the
	// answer in hand rather than on a later render (Curro, 2026-10-03).
	const ask = useCallback(async (ruleId = null) => {
		setState(s => ({ ...s, loading: true, error: null, ruleId }))
		try {
			const raw = await scheduleApi.preview(ruleId)
			setState({ raw, loading: false, error: null, ruleId })
			return { raw, error: null }
		} catch (e) {
			const error = describe(e)
			setState({ raw: null, loading: false, error, ruleId })
			return { raw: null, error }
		}
	}, [])

	return { ...state, ask }
}

export function useStrategyFeed() {
	const [state, setState] = useState({ strategies: [], loading: true, unavailable: false })

	useEffect(() => {
		let alive = true
		scheduleApi.strategies.list()
			.then(rows => alive && setState({
				strategies: rows.map(strategyForCalendar), loading: false, unavailable: false }))
			.catch(() => alive && setState({ strategies: [], loading: false, unavailable: true }))
		return () => { alive = false }
	}, [])

	return state
}

/**
 * What ran on each day of the visible month — the Calendar's summary of
 * past days (Curro, 2026-10-03). `{}` until it answers; `unavailable`
 * when the webback cannot say, so the cells show nothing rather than
 * "nothing ran".
 */
export function useExecutionsByDay(fromIso, toIso) {
	const [state, setState] = useState({ days: {}, loading: true, unavailable: false })

	useEffect(() => {
		if (!fromIso || !toIso) return undefined
		let alive = true
		setState(s => ({ ...s, loading: true }))
		scheduleApi.executionsByDay(fromIso, toIso)
			.then(b => alive && setState({ days: b?.days || {}, loading: false, unavailable: false }))
			.catch(() => alive && setState({ days: {}, loading: false, unavailable: true }))
		return () => { alive = false }
	}, [fromIso, toIso])

	return state
}
